import React, { useState, useEffect, useCallback } from "react";
import MyStudentsSkeleton from "../../components/layout/MyStudentsSkeleton";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
    Users, Check, X, ChevronRight, BarChart3,
    ClipboardList, TrendingUp, ArrowLeft, Clock,
    Calendar, MessageSquare, Star, Trash2, Send,
    Plus, Sparkles, GraduationCap,
    CheckCircle2, XCircle, ChevronDown, Briefcase,
    BookOpen, Network, Heart, UserCheck, Clipboard
} from "lucide-react";

function StatusBadge({ status }) {
    const config = {
        Completed: { cls: "bg-primary-100 text-primary-700", icon: "✓" },
        "All Done!": { cls: "bg-primary-100 text-primary-700", icon: "✓" },
        "In Progress": { cls: "bg-blue-100 text-blue-700", icon: "●" },
        "Not Started": { cls: "bg-slate-100 text-slate-600", icon: "○" },
    };
    const { cls, icon } = config[status] || { cls: "bg-slate-100 text-slate-600", icon: "○" };
    return (
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap inline-flex items-center gap-1 ${cls}`} role="status">
            <span aria-hidden="true">{icon}</span> {status || "Not Started"}
        </span>
    );
}

const focusAreaIcons = {
    "Job Performance": Briefcase,
    "Skilling": BookOpen,
    "Networking": Network,
    "Giving Back": Heart,
    "Feedback": MessageSquare,
    "Onboarding": UserCheck,
};

function FocusIcon({ name, size = 12, className = "" }) {
    const Icon = focusAreaIcons[name] || Clipboard;
    return <Icon size={size} className={className} />;
}

function TaskRow({ t, isDarkMode }) {
    const [open, setOpen] = React.useState(false);
    return (
        <div className={`rounded-xl border transition-all ${isDarkMode ? "bg-slate-800 border-slate-700 hover:border-slate-600" : "bg-white border-slate-200 hover:border-slate-300"}`}>
            <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer select-none" onClick={() => setOpen(!open)}>
                {/* Focus area icon */}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-500"}`}>
                    <FocusIcon name={t.FocusAreaName} size={14} />
                </div>

                {/* Title + inline badges */}
                <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                    <p className={`font-semibold text-sm truncate ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{t.TaskName}</p>
                </div>

                {/* Right side: status + chevron */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <StatusBadge status={t.Status} />
                    <ChevronDown size={14} className={`transition-transform ${isDarkMode ? "text-slate-500" : "text-slate-400"} ${open ? "rotate-180" : ""}`} />
                </div>
            </div>

            {/* Expandable details */}
            {open && (
                <div className={`px-3 pb-3 pt-0 border-t ${isDarkMode ? "border-slate-700/60" : "border-slate-100"}`}>
                    <div className="pt-2.5 space-y-2">
                        {t.Description && (
                            <p className={`text-xs leading-relaxed ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{t.Description}</p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md ${isDarkMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                                <FocusIcon name={t.FocusAreaName} size={10} /> {t.FocusAreaName || "General"}
                            </span>
                            {t.CreatedOn && (
                                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md ${isDarkMode ? "bg-slate-700 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
                                    <Clock size={10} /> {t.CreatedOn}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function MyStudents() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [mentees, setMentees] = useState([]);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [message, setMessage] = useState(null);

    // Student detail state
    const [selectedMentee, setSelectedMentee] = useState(null);
    const [studentTab, setStudentTab] = useState("overview");
    const [progress, setProgress] = useState(null);
    const [menteeTasks, setMenteeTasks] = useState([]);
    const [menteeFeedback, setMenteeFeedback] = useState([]);
    const [focusAreas, setFocusAreas] = useState([]);

    // Feedback form
    const [feedbackText, setFeedbackText] = useState("");
    const [feedbackRating, setFeedbackRating] = useState(0);
    const [feedbackTaskId, setFeedbackTaskId] = useState("");

    // Recommend task form
    const [showRecommendForm, setShowRecommendForm] = useState(false);
    const [recTitle, setRecTitle] = useState("");
    const [recDescription, setRecDescription] = useState("");
    const [recFocusAreaId, setRecFocusAreaId] = useState("");
    const [riskAlerts, setRiskAlerts] = useState([]);

    useEffect(() => {
        if (!user?.isCoach && !user?.isAdmin) navigate("/userdashboard");
    }, [user, navigate]);

    const fetchMentees = useCallback(async () => {
        try { const res = await api.get("/coach/my-mentees"); setMentees(res.data.mentees || []); } catch { setMentees([]); }
    }, []);

    const fetchRequests = useCallback(async () => {
        try { const res = await api.get("/coach/requests"); setRequests(res.data.requests || []); } catch { setRequests([]); }
    }, []);

    const fetchFocusAreas = useCallback(async () => {
        try { const res = await api.get("/task/focus-areas"); setFocusAreas(res.data.focusAreas || res.data || []); } catch { setFocusAreas([]); }
    }, []);

    useEffect(() => {
        const load = async () => { setLoading(true); await Promise.all([fetchMentees(), fetchRequests(), fetchFocusAreas()]); setLoading(false); };
        load();
    }, [fetchMentees, fetchRequests, fetchFocusAreas]);

    const fetchRiskAlerts = useCallback(async () => {
        try {
            const res = await api.get('/coach/risk-alerts?stale_days=7');
            setRiskAlerts(res.data.alerts || []);
        } catch {
            setRiskAlerts([]);
        }
    }, []);

    useEffect(() => {
        fetchRiskAlerts();
    }, [fetchRiskAlerts]);

    const showMsg = (text, type = "success") => { setMessage({ text, type }); setTimeout(() => setMessage(null), 4000); };

    const respondToRequest = async (requestId, action) => {
        setActionLoading(true);
        try {
            await api.post(`/coach/requests/${requestId}/respond`, { action });
            showMsg(`Request ${action}`);
            await Promise.all([fetchRequests(), fetchMentees()]);
        } catch (err) { showMsg(err.response?.data?.error || "Failed to respond", "error"); }
        finally { setActionLoading(false); }
    };

    const selectStudent = async (clientId) => {
        setSelectedMentee(clientId);
        setStudentTab("overview");
        try {
            const [progressRes, tasksRes, feedbackRes] = await Promise.all([
                api.get(`/coach/mentees/${clientId}/progress`),
                api.get(`/coach/mentees/${clientId}/tasks`),
                api.get(`/coach/feedback/mentee/${clientId}`),
            ]);
            setProgress(progressRes.data);
            setMenteeTasks(tasksRes.data.tasks || []);
            setMenteeFeedback(feedbackRes.data.feedback || []);
        } catch { setProgress(null); setMenteeTasks([]); setMenteeFeedback([]); }
    };

    const submitFeedback = async () => {
        if (!feedbackText.trim() || !selectedMentee) return;
        setActionLoading(true);
        try {
            await api.post("/coach/feedback", { client_id: selectedMentee, feedback_text: feedbackText.trim(), rating: feedbackRating || null, task_id: feedbackTaskId || null });
            showMsg("Feedback submitted");
            setFeedbackText(""); setFeedbackRating(0); setFeedbackTaskId("");
            const res = await api.get(`/coach/feedback/mentee/${selectedMentee}`);
            setMenteeFeedback(res.data.feedback || []);
        } catch (err) { showMsg(err.response?.data?.error || "Failed to submit feedback", "error"); }
        finally { setActionLoading(false); }
    };

    const submitRecommendedTask = async () => {
        if (!recTitle.trim() || !selectedMentee) return;
        setActionLoading(true);
        try {
            await api.post("/coach/suggest-task", { client_id: selectedMentee, title: recTitle.trim(), description: recDescription.trim(), focus_area_id: recFocusAreaId || null });
            showMsg("Task suggested!");
            setRecTitle(""); setRecDescription(""); setRecFocusAreaId(""); setShowRecommendForm(false);
            const tasksRes = await api.get(`/coach/mentees/${selectedMentee}/tasks`);
            setMenteeTasks(tasksRes.data.tasks || []);
        } catch (err) { showMsg(err.response?.data?.error || "Failed to suggest task", "error"); }
        finally { setActionLoading(false); }
    };

    const deleteFeedback = async (feedbackId) => {
        setActionLoading(true);
        try {
            await api.delete(`/coach/feedback/${feedbackId}`);
            showMsg("Feedback deleted");
            if (selectedMentee) { const res = await api.get(`/coach/feedback/mentee/${selectedMentee}`); setMenteeFeedback(res.data.feedback || []); }
        } catch (err) { showMsg(err.response?.data?.error || "Failed to delete", "error"); }
        finally { setActionLoading(false); }
    };


    const avatarColors = ["from-blue-500 to-indigo-600", "from-primary-500 to-teal-600", "from-violet-500 to-purple-600", "from-rose-500 to-pink-600", "from-amber-500 to-orange-600", "from-cyan-500 to-sky-600"];
    const getAvatarColor = (id) => avatarColors[(id || 0) % avatarColors.length];
    const card = `rounded-xl border p-4 transition-all ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`;

    if (loading) {
        return <MyStudentsSkeleton isDarkMode={isDarkMode} />;
    }

    // ── Student Detail View ──────────────
    if (selectedMentee && progress) {
        const { client, stats, recentTasks } = progress;
        const studentTabs = [
            { key: "overview", label: "Overview", icon: BarChart3 },
            { key: "tasks", label: "Tasks", icon: ClipboardList },
            { key: "feedback", label: "Feedback", icon: MessageSquare },
        ];

        return (
            <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
                <div className="max-w-5xl mx-auto px-4 py-8">
                    <button onClick={() => { setSelectedMentee(null); setProgress(null); }}
                        className={`flex items-center gap-2 mb-6 text-sm font-medium ${isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700"} transition-colors rounded-lg px-2 py-1 -ml-2`}>
                        <ArrowLeft size={16} /> Back to Students
                    </button>

                    {/* Student header */}
                    <div className={`rounded-2xl border overflow-hidden mb-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"} shadow-sm`}>
                        <div className="h-20 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 relative">
                            <div className="absolute -bottom-8 left-6">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold border-4 border-white dark:border-slate-800 shadow-lg">
                                    {client.FirstName?.[0]}{client.LastName?.[0]}
                                </div>
                            </div>
                        </div>
                        <div className="pt-12 px-6 pb-5">
                            <h1 className={`text-2xl font-extrabold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{client.FirstName} {client.LastName}</h1>
                            <p className={`text-sm mt-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{client.EmailAddress}</p>
                        </div>
                    </div>

                    {message && (
                        <div role="alert" className={`mb-5 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium ${
                            message.type === "error" ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                            : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                        }`}>{message.type === "error" ? <XCircle size={18} /> : <CheckCircle2 size={18} />} {message.text}</div>
                    )}

                    <div className={`flex gap-1 p-1.5 rounded-2xl mb-7 ${isDarkMode ? "bg-slate-800/70" : "bg-slate-100"}`} role="tablist">
                        {studentTabs.map((t) => (
                            <button key={t.key} role="tab" aria-selected={studentTab === t.key} onClick={() => setStudentTab(t.key)}
                                className={`flex items-center gap-1.5 px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                                    studentTab === t.key ? isDarkMode ? "bg-slate-700 text-white shadow-md" : "bg-white text-slate-900 shadow-md"
                                    : isDarkMode ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50" : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
                                }`}><t.icon size={15} /> {t.label}</button>
                        ))}
                    </div>

                    {/* Overview Tab */}
                    {studentTab === "overview" && (
                        <>
                            {(() => {
                                const s = stats;
                                return (
                                    <div className="grid grid-cols-3 gap-4 mb-8">
                                        {[{ label: "Total Tasks", value: s.totalTasks, icon: ClipboardList, bg: "from-blue-500 to-indigo-500", light: "bg-blue-50 text-blue-600" },
                                          { label: "Completed", value: s.completedTasks, icon: Check, bg: "from-primary-500 to-teal-500", light: "bg-primary-50 text-primary-600" },
                                          { label: "In Progress", value: s.inProgressTasks, icon: TrendingUp, bg: "from-amber-500 to-orange-500", light: "bg-amber-50 text-amber-600" }
                                        ].map((card) => (
                                            <div key={card.label} className={`rounded-2xl border p-4 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${isDarkMode ? `bg-gradient-to-br ${card.bg}` : card.light}`}>
                                                    <card.icon size={18} className={isDarkMode ? "text-white" : ""} />
                                                </div>
                                                <p className={`text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{card.value}</p>
                                                <p className={`text-xs font-medium mt-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{card.label}</p>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            <h2 className={`text-lg font-bold mb-3 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Recent Tasks</h2>
                            {recentTasks.length === 0 ? (
                                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No tasks found.</p>
                            ) : (
                                <div className="space-y-1.5">
                                    {recentTasks.map((t) => (
                                        <TaskRow key={t.TaskID} t={t} isDarkMode={isDarkMode} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Tasks Tab */}
                    {studentTab === "tasks" && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className={`text-lg font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Tasks ({menteeTasks.length})</h2>
                                <button onClick={() => setShowRecommendForm(!showRecommendForm)}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm">
                                    <Plus size={15} /> Suggest Task
                                </button>
                            </div>

                            {showRecommendForm && (
                                <div className={`${card} mb-5`}>
                                    <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>Suggest a Task</h3>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <input type="text" value={recTitle} onChange={(e) => setRecTitle(e.target.value)} placeholder="Task title *"
                                            className={`rounded-lg border p-2.5 text-sm ${isDarkMode ? "bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-500" : "bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400"}`} />
                                        <select value={recFocusAreaId} onChange={(e) => setRecFocusAreaId(e.target.value)}
                                            className={`select-styled rounded-xl border p-2.5 text-sm font-medium shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-600 text-slate-100" : "bg-white border-slate-200 text-slate-900"}`}>
                                            <option value="">Focus Area (optional)</option>
                                            {focusAreas.map((fa) => <option key={fa.FocusAreaID} value={fa.FocusAreaID}>{fa.FocusAreaName}</option>)}
                                        </select>
                                    </div>
                                    <textarea value={recDescription} onChange={(e) => setRecDescription(e.target.value)} placeholder="Description (optional)" rows={2}
                                        className={`w-full mt-3 rounded-lg border p-2.5 text-sm resize-none ${isDarkMode ? "bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-500" : "bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400"}`} />
                                    <div className="flex items-center justify-end mt-3 gap-2">
                                        <button onClick={() => setShowRecommendForm(false)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>Cancel</button>
                                        <button onClick={submitRecommendedTask} disabled={!recTitle.trim() || actionLoading}
                                            className="flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"><Send size={13} /> Suggest</button>
                                    </div>
                                </div>
                            )}

                            {menteeTasks.length === 0 ? (
                                <div className={`rounded-2xl border p-10 text-center ${isDarkMode ? "bg-slate-800/60 border-slate-700/60" : "bg-white border-slate-200"}`}>
                                    <ClipboardList className={`mx-auto mb-3 ${isDarkMode ? "text-slate-600" : "text-slate-300"}`} size={40} />
                                    <p className={`text-sm font-medium ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No tasks found.</p>
                                </div>
                            ) : (
                                <div className="space-y-1.5">
                                    {menteeTasks.map((t) => (
                                        <TaskRow key={t.TaskID} t={t} isDarkMode={isDarkMode} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Feedback Tab */}
                    {studentTab === "feedback" && (
                        <div>
                            <h2 className={`text-lg font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                <MessageSquare size={18} className="inline-block mr-2 -mt-0.5" />Feedback
                            </h2>
                            <div className={`${card} mb-5`}>
                                <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>Leave Feedback</h3>
                                <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} placeholder="Write feedback for this student..." rows={3}
                                    className={`w-full rounded-lg border p-3 text-sm resize-none ${isDarkMode ? "bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-500" : "bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400"}`} />
                                <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <div className="flex items-center gap-1">
                                            <span className={`text-xs mr-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Rating:</span>
                                            {[1, 2, 3, 4, 5].map((s) => (
                                                <button key={s} onClick={() => setFeedbackRating(s === feedbackRating ? 0 : s)}>
                                                    <Star size={18} className={s <= feedbackRating ? "text-amber-400 fill-amber-400" : isDarkMode ? "text-slate-600" : "text-slate-300"} />
                                                </button>
                                            ))}
                                        </div>
                                        <select value={feedbackTaskId} onChange={(e) => setFeedbackTaskId(e.target.value)}
                                            className={`select-styled text-xs rounded-xl border px-2.5 py-1.5 font-medium shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-600 text-slate-200" : "bg-white border-slate-300 text-slate-700"}`}>
                                            <option value="">Link to task (optional)</option>
                                            {menteeTasks.map((t) => <option key={t.TaskID} value={t.TaskID}>{t.TaskName}</option>)}
                                        </select>
                                    </div>
                                    <button onClick={submitFeedback} disabled={!feedbackText.trim() || actionLoading}
                                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                                        <Send size={14} /> Submit
                                    </button>
                                </div>
                            </div>

                            <h3 className={`text-base font-bold mb-3 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Previous Feedback ({menteeFeedback.length})</h3>
                            {menteeFeedback.length === 0 ? (
                                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No feedback yet.</p>
                            ) : (
                                <div className="space-y-3">
                                    {menteeFeedback.map((f) => (
                                        <div key={f.FeedbackID} className={card}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{f.FeedbackText}</p>
                                                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                                                        {f.Rating && <div className="flex items-center gap-0.5">{[1,2,3,4,5].map((s) => <Star key={s} size={12} className={s <= f.Rating ? "text-amber-400 fill-amber-400" : isDarkMode ? "text-slate-600" : "text-slate-300"} />)}</div>}
                                                        {f.TaskTitle && <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${isDarkMode ? "bg-cyan-900/30 text-cyan-300" : "bg-cyan-50 text-cyan-700"}`}>Re: {f.TaskTitle}</span>}
                                                        <span className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{f.CreatedOn}</span>
                                                    </div>
                                                </div>
                                                <button onClick={() => deleteFeedback(f.FeedbackID)} className="text-red-400 hover:text-red-600 ml-3 flex-shrink-0 rounded-lg p-1"><Trash2 size={14} /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Main Students List ───────────────
    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="rounded-2xl overflow-hidden mb-8 shadow-sm">
                    <div className={`relative px-6 py-8 ${isDarkMode ? "bg-gradient-to-r from-primary-900 via-teal-900 to-cyan-900" : "bg-gradient-to-r from-primary-600 via-teal-600 to-cyan-600"}`}>
                        <Sparkles className="absolute top-4 right-6 text-white/20" size={40} />
                        <div className="relative flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white shadow-lg border border-white/30">
                                <GraduationCap size={28} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-extrabold tracking-tight text-white">My Students</h1>
                                <p className="text-sm text-white/70">Manage your coaching students and pending requests</p>
                            </div>
                        </div>
                    </div>
                </div>

                {message && (
                    <div role="alert" className={`mb-5 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium ${
                        message.type === "error" ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                        : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>{message.text}</div>
                )}

                {riskAlerts.length > 0 && (
                    <div className={`mb-6 rounded-2xl border p-4 ${isDarkMode ? "bg-amber-900/20 border-amber-800/40" : "bg-amber-50 border-amber-200"}`}>
                        <h3 className={`font-bold text-sm mb-2 ${isDarkMode ? "text-amber-200" : "text-amber-800"}`}>Early-Risk Alerts (7+ days stalled)</h3>
                        <div className="space-y-1.5">
                            {riskAlerts.slice(0, 6).map((alert) => (
                                <div key={alert.clientId} className={`text-sm ${isDarkMode ? "text-amber-100" : "text-amber-900"}`}>
                                    {alert.firstName} {alert.lastName}: {alert.reason}
                                </div>
                            ))}
                        </div>
                    </div>
                )}


                {/* Pending Requests */}
                {requests.length > 0 && (
                    <div className="mb-8">
                        <h2 className={`text-lg font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                            Pending Requests ({requests.length})
                        </h2>
                        <div className="space-y-3">
                            {requests.map((r) => (
                                <div key={r.RequestID} className={`flex items-center justify-between rounded-2xl border p-5 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                                    <div className="flex items-center gap-4">
                                        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${getAvatarColor(r.RequestID)} flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0`}>
                                            {r.FirstName?.[0]}{r.LastName?.[0]}
                                        </div>
                                        <div>
                                            <p className={`font-bold text-[15px] ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{r.FirstName} {r.LastName}</p>
                                            <p className={`text-xs mt-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{r.EmailAddress} &middot; {r.RequestDate}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => respondToRequest(r.RequestID, "accepted")} disabled={actionLoading}
                                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"><Check size={14} /> Accept</button>
                                        <button onClick={() => respondToRequest(r.RequestID, "denied")} disabled={actionLoading}
                                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 ${isDarkMode ? "bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-800/50" : "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"}`}><X size={14} /> Deny</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Students Grid */}
                <h2 className={`text-lg font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                    Students ({mentees.length})
                </h2>
                {mentees.length === 0 ? (
                    <div className={`rounded-2xl border p-10 text-center ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
                        <Users className={`mx-auto mb-3 ${isDarkMode ? "text-slate-600" : "text-slate-300"}`} size={48} />
                        <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>No students yet</h3>
                        <p className={`text-sm ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>When students send you requests and you accept, they'll appear here.</p>
                    </div>
                ) : (
                    <div className="grid gap-4 sm:grid-cols-2">
                        {mentees.map((m) => (
                            <div key={m.ClientID} className={`group rounded-2xl border p-5 cursor-pointer hover:shadow-lg transition-all ${isDarkMode ? "bg-slate-800 border-slate-700 hover:border-slate-600" : "bg-white border-slate-200 hover:border-primary-300 hover:shadow-primary-100/50"}`}
                                onClick={() => selectStudent(m.ClientID)} role="button" tabIndex={0}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectStudent(m.ClientID); } }}>
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getAvatarColor(m.ClientID)} flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0`}>
                                        {m.FirstName?.[0]}{m.LastName?.[0]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <h3 className={`font-bold text-[15px] ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{m.FirstName} {m.LastName}</h3>
                                            <ChevronRight size={16} className={`transition-transform group-hover:translate-x-0.5 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                                        </div>
                                        <p className={`text-xs mt-0.5 truncate ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{m.EmailAddress}</p>
                                        <div className="flex items-center gap-3 mt-3">
                                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg ${isDarkMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}><Calendar size={11} /> {m.JoinedDate}</span>
                                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg ${isDarkMode ? "bg-primary-900/30 text-primary-400" : "bg-primary-50 text-primary-700"}`}><BarChart3 size={11} /> View Progress</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
